import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {composeMessage} from '../../consts/errors';
import {WebNotificationsService} from '../../services/web-notifications.service';
import {AuthService} from '../../../../services/auth.service';
import {NotificationsService} from '../../services/notifications.service';
import {TranslateService} from '../../services/translate.service';
import {ActivatedRoute, Router} from '@angular/router';
import {SocketService} from '../../../../services/socket.service';
import {forkJoin} from 'rxjs';
import {ConfirmationService} from '../../services/confirmation.service';

@Component({
    selector: 'npa-notification',
    templateUrl: './notification.component.html',
    styleUrls: ['./notification.component.styl']
})
export class NotificationComponent implements OnInit {
    MAX_NUMBER_PER_LOAD = 12;
    pageIndex = 0;
    isArchivedSectionActive: boolean;
    loaderContext: {
        general: boolean,
        [context: string]: boolean
    } = {general: undefined};
    totalNotifications: number;
    notifications: any[] = [];
    archivedNotifications: any[] = [];

    notificationsUnseen = 0;
    @Output() change = new EventEmitter<{
        unseen: number
    }>();

    constructor(private webNotificationService: WebNotificationsService,
                private auth: AuthService,
                private translate: TranslateService,
                private router: Router,
                private activatedRoute: ActivatedRoute,
                private socketService: SocketService,
                private confirmationService: ConfirmationService,
                private notificationsService: NotificationsService) {
    }

    ngOnInit() {
        this.auth.isLoggedIn.subscribe((isLoggedIn: boolean) => {
            if (isLoggedIn) {
                this.init();
                this.load();
            }
        });
    }

    init() {
        this.socketService.connection.then((socket) => {
            socket.on('message', (message) => {
                this.notifications.unshift({
                    target_recipient_uuid: message.target_recipient_uuid,
                    rendered_title_en: message.title_en,
                    rendered_title_prs: message.title_prs,
                    rendered_title_ps: message.title_ps,
                    rendered_message_en: message.body_en,
                    rendered_message_prs: message.body_prs,
                    rendered_message_ps: message.body_ps,
                    rendered_uri_path: message.uri_path,
                    source_uuid: message.source_uuid,
                    source_full_name: message.source_full_name,
                    meta: message.meta,
                    created_at: message.created_at
                });
                this.notificationsUnseen++;
                this.change.emit({unseen: this.notificationsUnseen});
            });
        });
    }

    load() {
        this.loaderContext.general = true;
        this.webNotificationService.load(this.auth.loggedInUser.username, this.notifications.length || this.MAX_NUMBER_PER_LOAD, 0)
            .subscribe((response) => {
                this.notifications = response.notifications;
                this.totalNotifications = response.total;
                this.notificationsUnseen = response.unseen;
                this.change.emit({unseen: this.notificationsUnseen});
                this.loaderContext.general = false;
            }, (error: HttpErrorResponse) => {
                this.loaderContext.general = false;
                this.notificationsService.error(composeMessage(error));
            });
        this.webNotificationService.loadArchived(this.auth.loggedInUser.username)
            .subscribe((notifications) => {
                this.archivedNotifications = notifications;
                this.loaderContext.general = false;
            }, (error: HttpErrorResponse) => {
                this.notificationsService.error(composeMessage(error));
                this.loaderContext.general = false;
            });
    }

    loadMore() {
        this.loaderContext.more = true;
        this.webNotificationService.load(this.auth.loggedInUser.username, this.MAX_NUMBER_PER_LOAD, ++this.pageIndex)
            .subscribe((response) => {
                this.notifications.push(...response.notifications);
                this.loaderContext.more = false;
            }, (error: HttpErrorResponse) => {
                this.loaderContext.more = false;
                this.notificationsService.error(composeMessage(error));
            });
    }

    loadArchived() {
        this.loaderContext.general = true;
        this.archivedNotifications = [];
        this.webNotificationService.loadArchived(this.auth.loggedInUser.username)
            .subscribe((notifications) => {
                this.archivedNotifications = notifications;
                this.loaderContext.general = false;
            }, (error: HttpErrorResponse) => {
                this.loaderContext.general = false;
                this.notificationsService.error(composeMessage(error));
            });
    }

    archive(id: number) {
        this.loaderContext[id] = true;
        this.webNotificationService.archive(this.auth.loggedInUser.username, id)
            .subscribe(() => {
                this.loaderContext[id] = false;
                this.notificationsUnseen--;
                this.change.emit({unseen: this.notificationsUnseen});
                this.load();
            }, (error: HttpErrorResponse) => {
                this.loaderContext[id] = false;
                this.notificationsService.error(composeMessage(error));
            });
    }

    archiveAllSeen() {
        this.confirmationService.openDialog({
            message: 'PAGES.VIEWS.NOTIFICATIONS.ARCHIVE_ALL_CONFIRM',
        }).then((confirm) => {
            if (!confirm.isConfirmed) {
                return;
            }
            this.loaderContext.general = true;
            this.webNotificationService.archiveAllSeen(this.auth.loggedInUser.username)
                .subscribe((response) => {
                    this.load();
                }, (error: HttpErrorResponse) => {
                    this.loaderContext.general = false;
                    this.notificationsService.error(composeMessage(error));
                });
        });
    }

    delete(id: number) {
        this.loaderContext[id] = true;
        this.loaderContext.isDeleting = true;
        this.webNotificationService.delete(this.auth.loggedInUser.username, id)
            .subscribe(() => {
                this.loaderContext[id] = false;
                this.loaderContext.isDeleting = false;
                this.load();
            }, (error: HttpErrorResponse) => {
                this.loaderContext[id] = false;
                this.loaderContext.isDeleting = false;
                this.notificationsService.error(composeMessage(error));
            });
    }

    navigateTo(renderedUriPath: string, id: number) {
        Promise.all([
            this.router.navigate([this.translate.lang, 'procurement-process']),
        ]).then(() => {
            this.router.navigateByUrl(this.translate.lang + renderedUriPath, {relativeTo: this.activatedRoute})
                .then(() => {
                    this.webNotificationService
                        .markSeen(this.auth.loggedInUser.username, id)
                        .subscribe((response) => {
                            this.notificationsUnseen--;
                            this.change.emit({unseen: this.notificationsUnseen});
                            const notificationId = this.notifications.findIndex((notification) => notification.id === id);
                            if (notificationId !== -1) {
                                this.notifications[notificationId].seen = true;
                            }
                        }, (error: HttpErrorResponse) => {
                            this.notificationsService.silentError(composeMessage(error));
                        });
                });
        });
    }

    extractMeta(metaText: any, property: string, subProperty?: string) {
        if (typeof metaText === 'object') {
            return metaText && metaText[property] && metaText[property][subProperty] ? metaText[property][subProperty] : 'n/a';
        }
        const meta = JSON.parse(metaText || '{}');
        return meta && meta[property] && meta[property][subProperty] ? meta[property][subProperty] : 'n/a';
    }
}
